import React, { useEffect, useState, useCallback } from "react";
import {
  Table, Tag, Button, Drawer, Form, Input, InputNumber, Select,
  message, Space, Image, Descriptions, Badge,
} from "antd";
import { PlusOutlined, EyeOutlined } from "@ant-design/icons";
import styled from "styled-components";
import customClientAxios from "../customAxios";

const TableCard = styled.div`
  background: white; border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  border: 1px solid #e2e8f0; overflow: hidden;
`;
const ToolbarRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px; flex-wrap: wrap; gap: 12px;
`;
const FilterRow = styled.div`
  display: flex; gap: 12px; flex-wrap: wrap;
`;
const PhotoGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 8px;
  margin-top: 16px;
`;
const PhotoThumb = styled.div`
  border-radius: 8px; overflow: hidden; aspect-ratio: 1;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const STATUS_COLORS = {
  created: "default", uploading: "processing", uploaded: "warning",
  review: "orange", published: "green", unpublished: "default", expired: "red",
};

function GalleryManagement() {
  const [galleries, setGalleries] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState(null);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [detailDrawer, setDetailDrawer] = useState({ open: false, gallery: null, photos: [], vendor: null });
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchGalleries = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const res = await customClientAxios().get("/admin/photo-cloud/galleries", { params });
      setGalleries(res.data.galleries || []);
      setPagination({ page: res.data.pagination.page, total: res.data.pagination.total });
    } catch {
      message.error("Failed to load galleries");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchVendors = useCallback(async () => {
    try {
      const res = await customClientAxios().get("/admin/photo-cloud/vendors", { params: { status: "active", limit: 100 } });
      setVendors(res.data.vendors || []);
    } catch {}
  }, []);

  useEffect(() => { fetchGalleries(); }, [fetchGalleries]);
  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({ "pricing.monthlyAmount": 499, "pricing.currency": "INR" });
    setCreateDrawerOpen(true);
  };

  const openDetail = async (gallery) => {
    try {
      const res = await customClientAxios().get(`/admin/photo-cloud/galleries/${gallery.galleryId}`);
      setDetailDrawer({
        open: true,
        gallery: res.data.gallery,
        photos: res.data.photos || [],
        vendor: res.data.vendor,
      });
    } catch {
      message.error("Failed to load gallery detail");
    }
  };

  const handleCreate = async (values) => {
    setSubmitting(true);
    try {
      await customClientAxios().post("/admin/photo-cloud/galleries", {
        brandPageId: values.brandPageId,
        featureItemId: values.featureItemId,
        vendorId: values.vendorId,
        hostPhone: values.hostPhone,
        hostEmail: values.hostEmail,
        hostName: values.hostName,
        pricing: {
          monthlyAmount: values["pricing.monthlyAmount"],
          currency: values["pricing.currency"],
        },
      });
      message.success("Gallery created — vendor assigned");
      setCreateDrawerOpen(false);
      fetchGalleries(pagination.page);
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to create gallery");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: "Event", dataIndex: "eventName", key: "eventName", width: 200, ellipsis: true,
      render: (text, record) => text || record.galleryId,
    },
    { title: "Vendor", dataIndex: "vendorName", key: "vendorName", width: 150 },
    { title: "Host", dataIndex: "hostName", key: "hostName", width: 150 },
    {
      title: "Status", dataIndex: "status", key: "status", width: 110,
      render: (s) => <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
    },
    { title: "Photos", dataIndex: "photoCount", key: "photoCount", width: 80, align: "center" },
    {
      title: "Price/mo", key: "pricing", width: 100,
      render: (_, r) => `${r.pricing?.currency || "INR"} ${r.pricing?.monthlyAmount || 0}`,
    },
    {
      title: "Created", dataIndex: "createdAt", key: "createdAt", width: 120,
      render: (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" }) : "-",
    },
    {
      title: "", key: "actions", width: 80,
      render: (_, record) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>View</Button>
      ),
    },
  ];

  return (
    <>
      <ToolbarRow>
        <FilterRow>
          <Select
            placeholder="Filter by status"
            allowClear
            style={{ width: 160 }}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            options={[
              { value: "created", label: "Created" },
              { value: "uploading", label: "Uploading" },
              { value: "uploaded", label: "Uploaded" },
              { value: "review", label: "In Review" },
              { value: "published", label: "Published" },
              { value: "expired", label: "Expired" },
            ]}
          />
          <span style={{ fontSize: 13, color: "#64748b", alignSelf: "center" }}>
            {pagination.total} galleries
          </span>
        </FilterRow>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Create Gallery</Button>
      </ToolbarRow>

      <TableCard>
        <Table
          dataSource={galleries}
          columns={columns}
          rowKey="galleryId"
          loading={loading}
          pagination={{
            current: pagination.page,
            total: pagination.total,
            pageSize: 20,
            showSizeChanger: false,
            onChange: (p) => fetchGalleries(p),
          }}
          size="middle"
          scroll={{ x: 1000 }}
        />
      </TableCard>

      {/* Create Gallery Drawer */}
      <Drawer
        title="Create Gallery"
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        width={480}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="brandPageId" label="Brand Page ID" rules={[{ required: true }]}>
            <Input placeholder="e.g. bp_abc123" />
          </Form.Item>
          <Form.Item name="featureItemId" label="Event Feature Item ID" rules={[{ required: true }]}>
            <Input placeholder="e.g. featureItem_xyz" />
          </Form.Item>
          <Form.Item name="vendorId" label="Assign Vendor" rules={[{ required: true }]}>
            <Select
              placeholder="Select vendor"
              options={vendors.map((v) => ({ value: v.vendorId, label: `${v.name} (${v.role})` }))}
              showSearch
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Form.Item name="hostName" label="Host Name">
            <Input />
          </Form.Item>
          <Form.Item name="hostPhone" label="Host Phone">
            <Input placeholder="e.g. 9876543210" />
          </Form.Item>
          <Form.Item name="hostEmail" label="Host Email">
            <Input />
          </Form.Item>
          <Space style={{ width: "100%" }} size="middle">
            <Form.Item name="pricing.monthlyAmount" label="Monthly Price" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="pricing.currency" label="Currency" style={{ width: 100 }}>
              <Select options={[{ value: "INR", label: "INR" }, { value: "USD", label: "USD" }]} />
            </Form.Item>
          </Space>
          <Button type="primary" htmlType="submit" loading={submitting} block>
            Create Gallery
          </Button>
        </Form>
      </Drawer>

      {/* Gallery Detail Drawer */}
      <Drawer
        title={detailDrawer.gallery?.eventName || "Gallery Detail"}
        open={detailDrawer.open}
        onClose={() => setDetailDrawer({ open: false, gallery: null, photos: [], vendor: null })}
        width={600}
        destroyOnClose
      >
        {detailDrawer.gallery && (
          <>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Status">
                <Badge status={detailDrawer.gallery.status === "published" ? "success" : "processing"} text={detailDrawer.gallery.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Photos">{detailDrawer.gallery.photoCount}</Descriptions.Item>
              <Descriptions.Item label="Vendor">{detailDrawer.vendor?.name || detailDrawer.gallery.vendorId}</Descriptions.Item>
              <Descriptions.Item label="Host">{detailDrawer.gallery.hostName || "-"}</Descriptions.Item>
              <Descriptions.Item label="Host Phone">{detailDrawer.gallery.hostPhone || "-"}</Descriptions.Item>
              <Descriptions.Item label="Price/mo">{detailDrawer.gallery.pricing?.currency} {detailDrawer.gallery.pricing?.monthlyAmount}</Descriptions.Item>
              <Descriptions.Item label="Slug" span={2}>{detailDrawer.gallery.slug}</Descriptions.Item>
              <Descriptions.Item label="Gallery ID" span={2} >
                <span style={{ fontSize: 11, fontFamily: "monospace" }}>{detailDrawer.gallery.galleryId}</span>
              </Descriptions.Item>
            </Descriptions>

            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              Photos ({detailDrawer.photos.length})
            </div>
            <PhotoGrid>
              {detailDrawer.photos.map((photo) => (
                <PhotoThumb key={photo.photoId}>
                  <Image src={photo.cdnUrl} alt={photo.originalFilename} style={{ objectFit: "cover" }} />
                </PhotoThumb>
              ))}
            </PhotoGrid>
            {detailDrawer.photos.length === 0 && (
              <div style={{ color: "#94a3b8", textAlign: "center", padding: 40 }}>No photos uploaded yet</div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}

export default GalleryManagement;
